import { parseFlexibleTimestamp } from '../fileParser';

/**
 * Reading instructions: how a file lays out its readings, separately from which
 * column holds them.
 *
 * Column detection describes a file one way — "the time is in column X and the
 * reading in column Y". Utilities do not all write that shape. The Opower
 * "Download my data" export (ComEd, PG&E) puts the date in one column and the
 * time of day in another, and can carry gas rows beside electric ones; some
 * utilities put each day on one row with its readings across 96 or 24 columns.
 * Each of those reached a customer as a refusal or, worse, as a confident wrong
 * peak (2026-10-21: a ComEd file refused as dated 1899; a gas reading reported
 * as the electric peak; a day-per-row file understated a hundredfold).
 *
 * A Layout names the shape. applyLayout() rewrites the sheet into the shape
 * detection already understands — one timestamp column, one reading per row —
 * and everything after it (profiling, column assignment, extraction, the Layer 0
 * invariants, the plausibility checks) runs unchanged. The same move the Green
 * Button reader makes: no format-specific path past this point.
 *
 * A Layout can come from detectLayout(), from the preparer's column picker, or
 * from a saved or AI-proposed set of instructions. Whoever wrote it, code applies
 * it and code computes every number.
 */

export type TimestampLayout =
  /** One column holds the whole timestamp. The ordinary case: nothing to rewrite. */
  | { kind: 'single' }
  /** A date-only column and a time-of-day column, joined into the date column. */
  | { kind: 'date-time'; dateColumn: number; timeColumn: number }
  /** One row per day, readings across the columns. */
  | {
      kind: 'day-row';
      headerRowIndex: number;
      dateColumn: number;
      /** Each reading column and the minutes after midnight its label names. */
      slots: Array<{ column: number; minutes: number }>;
      intervalMinutes: number;
      unit: 'kW' | 'kWh' | 'kVA';
      /** No unit was stated anywhere in the file; kWh was assumed and disclosed. */
      unitAssumed: boolean;
    };

/** Keep only the data rows whose `column` matches `keep` (a case-insensitive pattern). */
export interface RowFilter {
  column: number;
  keep: string;
}

export interface Layout {
  timestamp: TimestampLayout;
  rowFilter: RowFilter | null;
  /**
   * The sheet row holding the column headers, when instructions name it. Absent,
   * the classifier's header row is used. (A day-row layout carries its own.)
   */
  headerRowIndex?: number;
}

export interface AppliedLayout {
  rows: unknown[][];
  headerRow: (string | null)[] | null;
  dataStartRow: number;
  /** For a joined date and time: the column now holding the timestamp, and its label. */
  joined: { column: number; label: string } | null;
  /** Data rows a row filter set aside. */
  rowsFilteredOut: number;
  warnings: string[];
}

export const SINGLE_LAYOUT: Layout = { timestamp: { kind: 'single' }, rowFilter: null };

const SAMPLE_ROWS = 500;
/** Share of a column's non-blank cells that must have the shape, in the sample. */
const SHAPE_RATIO = 0.9;
const MS_PER_DAY = 86400000;
/** Day zero of the 1900 and 1904 date systems: where a time-only serial lands. */
const EXCEL_DAY_ZERO = [Date.UTC(1899, 11, 30), Date.UTC(1904, 0, 1)];
const CLOCK_ONLY_RE = /^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]\.?m\.?)?$/i;
/** A date with no time: "2023-08-18", "8/18/2023", "08-18-2023". */
const DATE_ONLY_TEXT_RE = /^(\d{4}-\d{1,2}-\d{1,2}|\d{1,2}[/-]\d{1,2}[/-]\d{2,4})$/;
/** A day-per-row header needs this many time-slot columns. */
const MIN_SLOT_COLUMNS = 12;
/** How far down the sheet to look for a day-per-row header, preamble included. */
const DAY_ROW_SCAN_ROWS = 40;
const SLOT_RANGE_RE = /^(\d{1,2}):(\d{2})\s*(?:[ap]\.?m\.?)?\s*[-–]\s*(\d{1,2}):(\d{2})\s*([ap]\.?m\.?)?$/i;
const SLOT_INDEX_RE = /^(he|hr|hour|int|interval|period|kwh|kw)\s*[-_#.]?\s*(\d{1,3})$/i;
const ELECTRIC_RE = /electric|elec\b/i;
const OTHER_SERVICE_RE = /gas|therm|water|propane|steam/i;

// ─── Cell shapes ─────────────────────────────────────────────────────────────

/** Milliseconds since midnight for a time-of-day cell, or null if it is not one. */
export function timeOfDayMs(cell: unknown): number | null {
  if (cell instanceof Date) {
    const t = cell.getTime();
    if (isNaN(t)) return null;
    const dayStart = Math.floor(t / MS_PER_DAY) * MS_PER_DAY;
    return EXCEL_DAY_ZERO.includes(dayStart) ? t - dayStart : null;
  }
  if (typeof cell !== 'string') return null;
  return clockMs(cell.trim());
}

function clockMs(text: string): number | null {
  const m = text.match(CLOCK_ONLY_RE);
  if (!m) return null;
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const second = m[3] ? Number(m[3]) : 0;
  const meridiem = m[4]?.toLowerCase().replace(/\./g, '');
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    hour = (hour % 12) + (meridiem === 'pm' ? 12 : 0);
  }
  // 24:00 is a legitimate end-of-day label in interval exports, and nothing later is.
  if (hour > 24 || minute > 59 || second > 59 || (hour === 24 && (minute > 0 || second > 0))) return null;
  return ((hour * 60 + minute) * 60 + second) * 1000;
}

/** UTC midnight of a date-only cell, or null if it is not one. */
export function dateOnlyMs(cell: unknown): number | null {
  if (cell instanceof Date) {
    const t = cell.getTime();
    if (isNaN(t) || t % MS_PER_DAY !== 0 || EXCEL_DAY_ZERO.includes(t)) return null;
    return t;
  }
  if (typeof cell !== 'string' || !DATE_ONLY_TEXT_RE.test(cell.trim())) return null;
  const d = parseFlexibleTimestamp(cell);
  if (!d) return null;
  const t = d.getTime();
  return t % MS_PER_DAY === 0 ? t : null;
}

function columnHasShape(rows: unknown[][], c: number, test: (cell: unknown) => number | null): boolean {
  let nonBlank = 0;
  let matching = 0;
  for (const row of rows) {
    const cell = row[c];
    if (cell === null || cell === undefined || cell === '') continue;
    nonBlank++;
    if (test(cell) !== null) matching++;
  }
  return nonBlank >= 2 && matching / nonBlank >= SHAPE_RATIO;
}

function hhmm(ms: number): string {
  const totalMinutes = Math.floor(ms / 60000);
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

function unitIn(text: string): 'kW' | 'kWh' | 'kVA' | null {
  if (/\bkwh\b|kilowatt[- ]?hours?/i.test(text)) return 'kWh';
  if (/\bkva\b/i.test(text)) return 'kVA';
  if (/\bkw\b|kilowatts?\b/i.test(text)) return 'kW';
  return null;
}

// ─── Day per row ─────────────────────────────────────────────────────────────

/**
 * What a header cell says about the time slot it labels: minutes after midnight
 * for a clock or range label, or an index for "HE7", "Interval 12", or a bare 7.
 */
type SlotLabel = { minutes: number; end: boolean } | { index: number } | null;

function slotLabelOf(cell: unknown): SlotLabel {
  if (cell instanceof Date) {
    const ms = timeOfDayMs(cell);
    return ms === null ? null : { minutes: ms / 60000, end: false };
  }
  if (typeof cell === 'number') return Number.isInteger(cell) && cell >= 0 && cell <= 100 ? { index: cell } : null;
  if (typeof cell !== 'string') return null;
  const s = cell.trim();
  const range = s.match(SLOT_RANGE_RE);
  if (range) {
    const end = clockMs(`${range[3]}:${range[4]}${range[5] ? ` ${range[5]}` : ''}`);
    return end === null ? null : { minutes: end / 60000, end: true };
  }
  const ms = clockMs(s);
  if (ms !== null) return { minutes: ms / 60000, end: false };
  const idx = s.match(SLOT_INDEX_RE);
  if (idx) return { index: Number(idx[2]) };
  if (/^\d{1,3}$/.test(s)) return { index: Number(s) };
  return null;
}

/**
 * The slot columns of a candidate header row and the minutes each names, or
 * null when the row is not a day's readings laid across.
 *
 * Clock labels are taken as written. A run of clocks from 00:15 to 24:00 names
 * interval ends; from 00:00 to 23:45, interval starts — either way the reading
 * lands on the instant its label names, as an `interval_ending_time` column's
 * readings do. Index labels ("HE1" … "HE24", "1" … "96") are positions: the
 * interval is the day divided by their count, and an index run starting at 1
 * names interval ends (hour ending 1 is 01:00), one starting at 0 names starts.
 */
function slotsOf(row: unknown[]): { slots: Array<{ column: number; minutes: number }>; intervalMinutes: number } | null {
  const labelled = row.map((cell, column) => ({ column, label: slotLabelOf(cell) })).filter(x => x.label !== null);

  const clocks = labelled.filter(x => x.label && 'minutes' in x.label) as Array<{ column: number; label: { minutes: number } }>;
  if (clocks.length >= MIN_SLOT_COLUMNS) {
    const slots = clocks.map(x => ({ column: x.column, minutes: x.label.minutes }));
    const interval = steadyStep(slots.map(s => s.minutes));
    return interval ? { slots, intervalMinutes: interval } : null;
  }

  // Longest run of consecutive indexes in adjacent columns.
  const indexed = labelled.filter(x => x.label && 'index' in x.label) as Array<{ column: number; label: { index: number } }>;
  let best: typeof indexed = [];
  let run: typeof indexed = [];
  for (const x of indexed) {
    const prev = run[run.length - 1];
    if (prev && x.column === prev.column + 1 && x.label.index === prev.label.index + 1) run.push(x);
    else run = x.label.index <= 1 ? [x] : [];
    if (run.length > best.length) best = [...run];
  }
  if (best.length < MIN_SLOT_COLUMNS || 1440 % best.length !== 0) return null;
  const interval = 1440 / best.length;
  return { slots: best.map(x => ({ column: x.column, minutes: x.label.index * interval })), intervalMinutes: interval };
}

/** The one step between successive values, when they step evenly; else null. */
function steadyStep(values: number[]): number | null {
  if (values.length < 2) return null;
  const step = values[1] - values[0];
  if (step <= 0 || 1440 % step !== 0) return null;
  for (let i = 2; i < values.length; i++) if (values[i] - values[i - 1] !== step) return null;
  return step;
}

function numericCount(row: unknown[]): number {
  let count = 0;
  for (const cell of row) {
    if (typeof cell === 'number' && Number.isFinite(cell)) count++;
    else if (typeof cell === 'string' && cell.trim() !== '' && Number.isFinite(Number(cell.replace(/,/g, '')))) count++;
  }
  return count;
}

/**
 * A day-per-row layout, when the sheet is one: a header row with 12+ time-slot
 * labels in its first 40 rows, rows below it carrying as many numbers, and one
 * column of dates beside them. `slotHeaderRow` reports a slot header even when no
 * date column could be found, so the caller can refuse by name.
 */
export function detectDayRow(rows: unknown[][]): { layout: TimestampLayout | null; slotHeaderRow: number | null; slotCount: number } {
  const scan = rows.slice(0, DAY_ROW_SCAN_ROWS);
  for (let r = 0; r < scan.length; r++) {
    const found = slotsOf((scan[r] ?? []) as unknown[]);
    if (!found) continue;
    const below = rows.slice(r + 1, r + 1 + SAMPLE_ROWS) as unknown[][];
    if (!below.slice(0, 5).some(b => numericCount(b ?? []) >= MIN_SLOT_COLUMNS)) continue;

    const slotColumns = new Set(found.slots.map(s => s.column));
    const width = Math.max(...below.map(b => (b ?? []).length), 0);
    let dateColumn: number | null = null;
    for (let c = 0; c < width && dateColumn === null; c++) {
      if (!slotColumns.has(c) && columnHasShape(below, c, dateOnlyMs)) dateColumn = c;
    }
    if (dateColumn === null) return { layout: null, slotHeaderRow: r, slotCount: found.slots.length };

    // The unit: a column of unit words beside the readings, else any unit named
    // in the header row or the preamble above it, else kWh — what day-per-row
    // exports carry — assumed and disclosed.
    let unit: 'kW' | 'kWh' | 'kVA' | null = null;
    for (let c = 0; c < width && !unit; c++) {
      if (slotColumns.has(c) || c === dateColumn) continue;
      const words = below.map(b => (b ?? [])[c]).filter(v => typeof v === 'string') as string[];
      const units = new Set(words.map(w => unitIn(` ${w} `)).filter(Boolean));
      if (units.size === 1 && words.length >= 2) unit = [...units][0];
    }
    if (!unit) {
      const text = rows.slice(0, r + 1).flat().filter(v => typeof v === 'string').join(' ');
      unit = unitIn(text);
    }
    return {
      layout: {
        kind: 'day-row',
        headerRowIndex: r,
        dateColumn,
        slots: found.slots,
        intervalMinutes: found.intervalMinutes,
        unit: unit ?? 'kWh',
        unitAssumed: unit === null,
      },
      slotHeaderRow: r,
      slotCount: found.slots.length,
    };
  }
  return { layout: null, slotHeaderRow: null, slotCount: 0 };
}

// ─── Row filter ──────────────────────────────────────────────────────────────

/**
 * A column naming the service on each row, when the file mixes electric rows
 * with gas, water or another service: the Opower export's TYPE column reads
 * "Electric usage" or "Natural gas usage". Only electric rows are kept.
 */
function detectRowFilter(dataRows: unknown[][]): RowFilter | null {
  const sample = dataRows.slice(0, SAMPLE_ROWS * 4);
  const width = Math.max(...sample.map(r => r.length), 0);
  for (let c = 0; c < width; c++) {
    const values = new Set<string>();
    let text = 0;
    let nonBlank = 0;
    for (const row of sample) {
      const cell = row[c];
      if (cell === null || cell === undefined || cell === '') continue;
      nonBlank++;
      if (typeof cell === 'string') { text++; values.add(cell.trim()); }
      if (values.size > 6) break;
    }
    if (values.size < 2 || values.size > 6 || text !== nonBlank) continue;
    const list = [...values];
    if (list.some(v => ELECTRIC_RE.test(v)) && list.some(v => OTHER_SERVICE_RE.test(v) && !ELECTRIC_RE.test(v))) {
      return { column: c, keep: ELECTRIC_RE.source };
    }
  }
  return null;
}

// ─── Date and time in separate columns ───────────────────────────────────────

function timeColumnsOf(sample: unknown[][]): number[] {
  const width = Math.max(...sample.map(r => r.length), 0);
  const cols: number[] = [];
  for (let c = 0; c < width; c++) {
    if (!columnHasShape(sample, c, dateOnlyMs) && columnHasShape(sample, c, timeOfDayMs)) cols.push(c);
  }
  return cols;
}

/**
 * A lone date-only column plus time-of-day column(s). With several time columns
 * the interval start is used: a START/END pair labels the same interval, and the
 * start always falls on the row's date, where an end of "00:00" belongs to the next.
 */
function detectDateTime(sample: unknown[][], headerRow: (string | null)[] | null): TimestampLayout | null {
  const width = Math.max(...sample.map(r => r.length), 0);
  const dateCols: number[] = [];
  for (let c = 0; c < width; c++) if (columnHasShape(sample, c, dateOnlyMs)) dateCols.push(c);
  const timeCols = timeColumnsOf(sample);
  if (dateCols.length !== 1 || timeCols.length === 0) return null;
  const header = (c: number) => headerRow?.[c] ?? '';
  const timeColumn =
    timeCols.find(c => /start|begin|from/i.test(header(c))) ??
    timeCols.find(c => !/end|to\b|thru|through/i.test(header(c))) ??
    timeCols[0];
  return { kind: 'date-time', dateColumn: dateCols[0], timeColumn };
}

/**
 * Every date-only column paired with every time-of-day column, for the reading
 * search to try when the detected layout's reading does not hold together.
 */
export function dateTimeLayoutsOf(rows: unknown[][], dataStartRow: number, rowFilter: RowFilter | null): Layout[] {
  const dataRows = rows.slice(dataStartRow);
  const sample = (rowFilter ? dataRows.filter(r => keeps(rowFilter, r)) : dataRows).slice(0, SAMPLE_ROWS);
  if (sample.length < 2) return [];
  const width = Math.max(...sample.map(r => r.length), 0);
  const dateCols: number[] = [];
  for (let c = 0; c < width; c++) if (columnHasShape(sample, c, dateOnlyMs)) dateCols.push(c);
  const layouts: Layout[] = [];
  for (const dateColumn of dateCols) {
    for (const timeColumn of timeColumnsOf(sample)) {
      layouts.push({ timestamp: { kind: 'date-time', dateColumn, timeColumn }, rowFilter });
    }
  }
  return layouts;
}

// ─── Detection and application ───────────────────────────────────────────────

/** The layout the sheet appears to use. `SINGLE_LAYOUT` when nothing needs rewriting. */
export function detectLayout(rows: unknown[][], headerRow: (string | null)[] | null, dataStartRow: number): Layout {
  const dayRow = detectDayRow(rows).layout;
  if (dayRow) return { timestamp: dayRow, rowFilter: null };

  const dataRows = rows.slice(dataStartRow);
  const rowFilter = detectRowFilter(dataRows);
  const kept = rowFilter ? dataRows.filter(r => keeps(rowFilter, r)) : dataRows;
  const sample = kept.slice(0, SAMPLE_ROWS);
  if (sample.length < 2) return { timestamp: { kind: 'single' }, rowFilter };
  return { timestamp: detectDateTime(sample, headerRow) ?? { kind: 'single' }, rowFilter };
}

function keeps(filter: RowFilter, row: unknown[]): boolean {
  return new RegExp(filter.keep, 'i').test(String(row[filter.column] ?? ''));
}

/** The label for a column: its header, or its letter position when it has none. */
function labelOf(headerRow: (string | null)[] | null, c: number): string {
  return headerRow?.[c]?.trim() || `column ${c + 1}`;
}

/**
 * Rewrite the sheet into one timestamp column and one reading per row.
 *
 * date-time: the date column becomes the joined timestamp; every time-of-day
 * column becomes plain "HH:MM" text, so nothing downstream mistakes a time of
 * day for a timestamp. Rows missing either half keep their cells and are later
 * skipped and counted like any unreadable row.
 *
 * day-row: a new sheet of [timestamp, reading, unit], one row per slot, under
 * headers that say what was read ("Date + 96 interval columns").
 */
export function applyLayout(
  rows: unknown[][],
  headerRow: (string | null)[] | null,
  dataStartRow: number,
  layout: Layout
): AppliedLayout {
  const warnings: string[] = [];
  const ts = layout.timestamp;

  if (ts.kind === 'day-row') {
    const n = ts.slots.length;
    const out: unknown[][] = [[`${labelOf(rows[ts.headerRowIndex] as (string | null)[], ts.dateColumn)} + ${n} interval columns`, `${ts.unit} in ${n} interval columns`, 'Unit']];
    for (const row of rows.slice(ts.headerRowIndex + 1) as unknown[][]) {
      const day = dateOnlyMs(row?.[ts.dateColumn]);
      if (day === null) continue;
      for (const slot of ts.slots) {
        const cell = row[slot.column];
        if (cell === null || cell === undefined || cell === '') continue;
        out.push([new Date(day + slot.minutes * 60000), cell, ts.unit]);
      }
    }
    if (ts.unitAssumed) {
      warnings.push(
        `This file states no unit for its readings. TrueLoad read them as kWh per ${ts.intervalMinutes}-minute interval, ` +
        'the unit day-per-row exports use. If they are kW, the peak shown is too high.'
      );
    }
    return { rows: out, headerRow: out[0] as string[], dataStartRow: 1, joined: null, rowsFilteredOut: 0, warnings };
  }

  let working = rows;
  let rowsFilteredOut = 0;
  if (layout.rowFilter) {
    const filter = layout.rowFilter;
    working = rows.filter((row, r) => r < dataStartRow || keeps(filter, row));
    rowsFilteredOut = rows.length - working.length;
    if (rowsFilteredOut > 0) {
      warnings.push(
        `${rowsFilteredOut} rows for another service (gas, water) in '${labelOf(headerRow, filter.column)}' were set aside; only electric rows were read.`
      );
    }
  }

  if (ts.kind === 'date-time') {
    const timeCols = new Set([ts.timeColumn, ...timeColumnsOf(working.slice(dataStartRow, dataStartRow + SAMPLE_ROWS))]);
    working = working.map((row, r) => {
      if (r < dataStartRow) return row;
      const out = [...row];
      const day = dateOnlyMs(row[ts.dateColumn]);
      const time = timeOfDayMs(row[ts.timeColumn]);
      if (day !== null && time !== null) out[ts.dateColumn] = new Date(day + time);
      for (const c of timeCols) {
        const t = timeOfDayMs(row[c]);
        if (t !== null) out[c] = hhmm(t);
      }
      return out;
    });
    return {
      rows: working,
      headerRow,
      dataStartRow,
      joined: { column: ts.dateColumn, label: `${labelOf(headerRow, ts.dateColumn)} + ${labelOf(headerRow, ts.timeColumn)}` },
      rowsFilteredOut,
      warnings,
    };
  }

  return { rows: working, headerRow, dataStartRow, joined: null, rowsFilteredOut, warnings };
}
